import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageItemType, Playlist, TransitionType } from '../types';
import { Page } from './Page';
import { ThemeWrapper } from './ThemeWrapper';
import { ForceDisconnectButton } from './ForceDisconnectButton';
import { DeviceIdDialog } from './DeviceIdDialog';
import { PlaybackProvider } from './PlaybackContext';

interface PlayListProps {
  playlist: Playlist;
}

const PLAY_FULL_VIDEO_DURATION = 0;
function PlayList({ playlist }: PlayListProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [theme, setTheme] = useState(playlist?.playlistItems?.[currentIndex]?.page?.theme?.css);
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const activeIndexRef = useRef(0);

  const sortedPlaylistItems = useMemo(
    () => [...(playlist?.playlistItems ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [playlist?.playlistItems],
  );
  const playlistLength = sortedPlaylistItems.length;
  const currentPlaylistItem = sortedPlaylistItems?.[currentIndex];
  const nextIndex = currentIndex === playlistLength - 1 ? 0 : currentIndex + 1;
  const nextPlaylistItem = sortedPlaylistItems?.[nextIndex];
  const currentHasVideo =
    currentPlaylistItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false;
  const nextHasVideo =
    nextPlaylistItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false;

  const transitionIn = sortedPlaylistItems?.[currentIndex]?.transitionIn ?? TransitionType.Ease;
  const useOpacityTransition = transitionIn !== 'linear' && !currentHasVideo && !nextHasVideo;
  const useFullVideoDuration = currentHasVideo && currentPlaylistItem?.duration === PLAY_FULL_VIDEO_DURATION;

  const clearAdvanceTimeout = useCallback(() => {
    clearTimeout(advanceTimeoutRef.current);
  }, []);

  const moveToIndex = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentItem = sortedPlaylistItems[fromIndex];
      const nextItem = sortedPlaylistItems[toIndex];
      const nextTheme = nextItem?.page?.theme?.css ?? undefined;
      const transitionHasVideo =
        (currentItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false) ||
        (nextItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false);
      const themeDelay = transitionHasVideo || currentItem?.transitionOut === 'linear' ? 0 : 50;

      setCurrentIndex(toIndex);

      if (nextItem?.page?.theme?.themeId !== currentItem?.page?.theme?.themeId) {
        setTimeout(() => {
          setTheme(nextTheme);
        }, themeDelay);
      }
    },
    [sortedPlaylistItems],
  );

  const moveToNextFrom = useCallback(
    (fromIndex: number) => {
      if (playlistLength === 0 || fromIndex !== activeIndexRef.current) {
        return;
      }

      const toIndex = fromIndex === playlistLength - 1 ? 0 : fromIndex + 1;
      moveToIndex(fromIndex, toIndex);
    },
    [moveToIndex, playlistLength],
  );

  const handleVideoEnded = useCallback(() => {
    if (!useFullVideoDuration) {
      return;
    }

    clearAdvanceTimeout();
    moveToNextFrom(currentIndex);
  }, [clearAdvanceTimeout, currentIndex, moveToNextFrom, useFullVideoDuration]);

  const playbackValue = useMemo(() => ({
    useFullVideoDuration,
    onVideoEnded: handleVideoEnded,
  }), [useFullVideoDuration, handleVideoEnded]);

  useEffect(() => {
    activeIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    setCurrentIndex(0);
    setTheme(sortedPlaylistItems[0]?.page?.theme?.css);
    activeIndexRef.current = 0;
    clearAdvanceTimeout();
  }, [clearAdvanceTimeout, playlist?.playlistId, sortedPlaylistItems]);

  useEffect(() => {
    if (playlistLength === 0) {
      clearAdvanceTimeout();
      return;
    }

    clearAdvanceTimeout();

    if (useFullVideoDuration) {
      return () => {
        clearAdvanceTimeout();
      };
    }

    const duration = currentPlaylistItem?.duration && currentPlaylistItem.duration > PLAY_FULL_VIDEO_DURATION
      ? currentPlaylistItem.duration
      : 10;

    advanceTimeoutRef.current = setTimeout(() => {
      moveToNextFrom(currentIndex);
    }, duration * 1000);

    return () => {
      clearAdvanceTimeout();
    };
  }, [
    clearAdvanceTimeout,
    currentIndex,
    currentPlaylistItem?.duration,
    moveToNextFrom,
    playlistLength,
    useFullVideoDuration,
  ]);

  return (
    <>
      <div className="w-full h-screen bg-black">
        <PlaybackProvider value={playbackValue}>
          {sortedPlaylistItems?.map((playlistItem, index) => {
            const page = playlistItem?.page;
            const isActivePage = index === currentIndex;
            const wrapperIdentity =
              playlistItem?.playlistItemId !== undefined
                ? playlistItem.playlistItemId.toString()
                : `${playlistItem?.pageId ?? 'page'}_${index}`;
            const wrapperId = `page_wrapper_${wrapperIdentity}`;

            const pageTheme = isActivePage && useOpacityTransition ? theme : page?.theme?.css;
            let resolvedThemeConfig: { theme?: { background?: string } } | null = null;
            try {
              resolvedThemeConfig = (typeof pageTheme === 'string' ? JSON.parse(pageTheme) : pageTheme) as { theme?: { background?: string } } | null;
            } catch {
              // Malformed theme JSON - skip background extraction.
            }
            const themeBackground = resolvedThemeConfig?.theme?.background;
            const pageBackground = themeBackground
              ? themeBackground.startsWith('http') ? `url(${themeBackground})` : themeBackground
              : undefined;

            return (
              <div
                id={wrapperId}
                className={`
                 absolute top-0 left-0 w-full h-full
                 ${isActivePage ? 'opacity-100  z-10' : 'opacity-0 z-0'}
                 ${useOpacityTransition ? 'duration-1000 transition-opacity ' : ' '}
               `}
                style={{ background: pageBackground, backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }}
                key={wrapperIdentity}
              >
                <ThemeWrapper key={wrapperIdentity} config={pageTheme as any}>
                  {page && (
                    <Page
                      page={page}
                      isActive={isActivePage}
                    />
                  )}
                </ThemeWrapper>
              </div>
            );
          })}
        </PlaybackProvider>
      </div>
      <DeviceIdDialog />
      <ForceDisconnectButton />
    </>
  );
}

export default PlayList;
